/**
 * cascade.js - Attribute resolution walking up the DOM
 *
 * Implements CSS-like inheritance for data-* attributes.
 * Children inherit from ancestors; explicit attributes override.
 */

import { getConfigForElement } from './config.js';

// Built-in defaults
export const DEFAULTS = {
  swap: 'inner',
  transitions: 'true',
  debounce: 300
};

// Attributes that cascade down the DOM
const INHERITABLE = new Set([
  'target',
  'swap',
  'select',
  'push',
  'replace',
  'confirm',
  'transitions',
  'prefetch',
  'cache',
  'cacheStorage',
  'sendHeaders',
  'instant',
  'errorTarget',
  'loadingTarget',
  'loadingClass',
  'queue'
]);

/**
 * Convert kebab-case to camelCase for dataset access
 * e.g., 'persist-scope' -> 'persistScope'
 */
function toCamelCase(str) {
  return str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

/**
 * Resolve an attribute value by walking up the DOM tree
 *
 * Priority (lowest to highest):
 * 1. Built-in defaults
 * 2. <meta name="htmlstar:*"> + JSON config defaults/selectors
 * 3. <html>/<body>/ancestor data-* (nearest wins)
 * 4. Element's own data-*
 *
 * @param {HTMLElement} el - Starting element
 * @param {string} attr - Attribute name (without data- prefix, kebab-case ok)
 * @returns {string|null} Resolved value or null
 */
export function resolve(el, attr) {
  const camelAttr = toCamelCase(attr);
  const isInheritable = INHERITABLE.has(attr) || INHERITABLE.has(camelAttr);

  // For non-inheritable attributes, only check the element itself
  if (!isInheritable) {
    if (el.dataset && el.dataset[camelAttr] !== undefined) {
      return el.dataset[camelAttr];
    }
    const configValue = getConfigForElement(el, attr);
    if (configValue !== undefined) return configValue;
    return DEFAULTS[camelAttr] ?? DEFAULTS[attr] ?? null;
  }

  // Walk up DOM for inheritable attributes
  let node = el;
  while (node && node !== document) {
    if (node.dataset && node.dataset[camelAttr] !== undefined) {
      return node.dataset[camelAttr];
    }
    node = node.parentElement;
  }

  // Check config defaults/selectors (meta + JSON config)
  const configValue = getConfigForElement(el, attr);
  if (configValue !== undefined) return configValue;

  // Return default or null
  return DEFAULTS[camelAttr] ?? DEFAULTS[attr] ?? null;
}

/**
 * Check if an attribute has a truthy value
 * Boolean attributes can be present without value (data-push)
 * or with explicit value (data-push="true")
 *
 * @param {HTMLElement} el - Element to check
 * @param {string} attr - Attribute name
 * @returns {boolean}
 */
export function hasAttr(el, attr) {
  const value = resolve(el, attr);
  if (value === null) return false;
  if (value === '') return true; // Boolean attribute present
  if (value === 'false') return false;
  return true;
}

/**
 * Resolve all relevant attributes for an element
 * Useful for debugging or getting a complete picture
 *
 * @param {HTMLElement} el - Element to resolve
 * @returns {Object} Object with all resolved attributes
 */
export function resolveAll(el) {
  const attrs = [
    'target', 'swap', 'push', 'replace', 'confirm', 'transitions',
    'trigger', 'select', 'persist', 'persistScope',
    'href', 'method', 'headers', 'include',
    'sse', 'poll', 'oob', 'disable',
    'prefetch', 'cache', 'cacheStorage', 'sendHeaders', 'instant',
    'errorTarget', 'loadingTarget', 'loadingClass', 'queue'
  ];

  const result = {};
  for (const attr of attrs) {
    const value = resolve(el, attr);
    if (value !== null) {
      result[attr] = value;
    }
  }
  return result;
}
